import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import { useHotel } from "../../contexts/HotelContext";
import { useAdminTenantDeletions } from "../../hooks/useAdminTenantDeletion";
import type {
  AdminTenantDeletionListItem,
  DeleteStatus,
} from "../../services/tenantDeletionApi";
import {
  DeleteStatusBadge,
  SnapshotBadge,
} from "../../components/admin/tenant-deletion/StatusBadges";
import { TenantDeletionSpinner } from "../../components/admin/tenant-deletion/TenantDeletionSpinner";

const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 200;

const formatDateTime = (iso: string | null): string => {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("pl-PL", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const truncate = (s: string | null, len: number): string => {
  if (!s) return "-";
  if (s.length <= len) return s;
  return s.slice(0, len) + "…";
};

const formatDeletedBy = (db: { name: string; email: string }): string => {
  if (db.name && db.name.trim().length > 0) {
    return `${db.name} (${db.email})`;
  }
  return db.email;
};

const STATUS_LABEL: Record<DeleteStatus, string> = {
  PENDING_DELETE: "W trakcie",
  DELETED: "Usunięty",
  FAILED: "Błąd",
};

/**
 * Read-only platform admin tenant deletions archive list (AC-29, AC-32).
 *
 * - Endpoint: GET /admin/tenant-deletions
 * - Sortowanie po deletedAt DESC, paginacja
 * - Empty state, BRAK przycisku Restore
 * - Sekcja info: "Snapshot purgowany po 30 dniach. Restore przez DevOps ticket."
 * - Klik wiersz → /admin/tenant-deletions/:id (szczegóły)
 */
const AdminTenantDeletions: React.FC = () => {
  const { user } = useHotel();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [deleteStatusFilter, setDeleteStatusFilter] = useState<
    DeleteStatus | undefined
  >(undefined);

  const query = useMemo(
    () => ({
      page,
      limit: Math.min(limit, MAX_LIMIT),
      sortBy: "deletedAt" as const,
      sortOrder,
      deleteStatus: deleteStatusFilter,
    }),
    [page, limit, sortOrder, deleteStatusFilter]
  );

  const { data, isLoading, isError, error } = useAdminTenantDeletions(query);

  if (user?.role !== "admin") {
    return (
      <Layout>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <h3 className="text-amber-800 font-medium">Brak dostępu</h3>
          <p className="text-amber-700 text-sm mt-1">
            Ta strona jest dostępna tylko dla globalnych administratorów.
          </p>
        </div>
      </Layout>
    );
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Archiwum usunięć tenantów
          </h1>
          <p className="text-neutral-600 mt-1">
            Read-only audit log operacji usuwania tenantów.
          </p>
        </div>

        {/* Info banner */}
        <div
          data-testid="admin-tenant-deletions-info"
          className="bg-primary-50 border border-primary-200 rounded-md p-4 text-sm text-primary-900"
        >
          Snapshot purgowany po 30 dniach. Restore przez DevOps ticket.
        </div>

        {/* Filters */}
        <div className="flex gap-3 items-end">
          <label className="text-sm text-neutral-700">
            Status:&nbsp;
            <select
              value={deleteStatusFilter ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setDeleteStatusFilter(
                  v === "" ? undefined : (v as DeleteStatus)
                );
                setPage(1);
              }}
              data-testid="admin-tenant-deletions-status-filter"
              className="border border-neutral-300 rounded-md px-2 py-1 text-sm"
            >
              <option value="">Wszystkie</option>
              <option value="PENDING_DELETE">W trakcie</option>
              <option value="DELETED">Usunięty</option>
              <option value="FAILED">Błąd</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => {
              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
              setPage(1);
            }}
            data-testid="admin-tenant-deletions-sort-toggle"
            className="rounded-md border border-neutral-300 bg-white px-3 py-1 text-sm hover:bg-neutral-50"
          >
            Sort: {sortOrder === "desc" ? "Najnowsze" : "Najstarsze"}
          </button>
        </div>

        {/* Filter context banner — visible only when status filter is active */}
        {deleteStatusFilter && (
          <div
            data-testid="admin-tenant-deletions-filter-context"
            className="text-sm text-neutral-700"
          >
            Filtr statusu:{" "}
            <span className="font-medium">
              {STATUS_LABEL[deleteStatusFilter]}
            </span>
            <button
              type="button"
              onClick={() => {
                setDeleteStatusFilter(undefined);
                setPage(1);
              }}
              className="ml-2 text-primary-600 hover:underline text-xs"
            >
              (wyczyść)
            </button>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center min-h-32">
            <TenantDeletionSpinner
              data-testid="admin-tenant-deletions-loading"
              size="md"
              variant="primary"
            />
          </div>
        )}

        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-red-800 font-medium">Błąd ładowania archiwum</h3>
            <p className="text-red-600 text-sm mt-1">
              {error instanceof Error ? error.message : "Nieznany błąd"}
            </p>
          </div>
        )}

        {!isLoading && !isError && data && (
          <>
            {data.items.length === 0 ? (
              <div
                data-testid="admin-tenant-deletions-empty"
                className="bg-white border border-neutral-200 rounded-md p-8 text-center text-neutral-500"
              >
                Brak archiwów. Pierwsza operacja usunięcia pojawi się tutaj.
              </div>
            ) : (
              <div className="overflow-x-auto bg-white rounded-md border border-neutral-200">
                <table
                  className="min-w-full divide-y divide-neutral-200"
                  data-testid="admin-tenant-deletions-table"
                >
                  <thead className="bg-neutral-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                      >
                        Tenant
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                      >
                        NIP
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                      >
                        Usunął
                      </th>
                      <th
                        scope="col"
                        aria-sort={
                          sortOrder === "desc" ? "descending" : "ascending"
                        }
                        className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                      >
                        Data
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                      >
                        Powód
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                      >
                        Snapshot
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                      >
                        Purge po
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {data.items.map((row: AdminTenantDeletionListItem) => (
                      <tr
                        key={row.id}
                        data-testid={`admin-tenant-deletions-row-${row.id}`}
                        className="hover:bg-neutral-50"
                      >
                        <td className="px-4 py-2 text-sm">
                          <Link
                            to={`/admin/tenant-deletions/${row.id}`}
                            className="text-primary-600 hover:underline"
                          >
                            {row.tenantNameSnapshot}
                          </Link>
                        </td>
                        <td className="px-4 py-2 text-sm text-neutral-600">
                          {row.tenantNipSnapshot || "-"}
                        </td>
                        <td className="px-4 py-2 text-sm text-neutral-600">
                          {formatDeletedBy(row.deletedBy)}
                        </td>
                        <td className="px-4 py-2 text-sm text-neutral-600">
                          {formatDateTime(row.deletedAt)}
                        </td>
                        <td
                          className="px-4 py-2 text-sm text-neutral-600 max-w-xs"
                          title={row.reason ?? ""}
                          aria-label={
                            row.reason && row.reason.length > 100
                              ? row.reason
                              : undefined
                          }
                        >
                          {truncate(row.reason, 100)}
                        </td>
                        <td className="px-4 py-2">
                          <DeleteStatusBadge status={row.deleteStatus} />
                        </td>
                        <td className="px-4 py-2">
                          <SnapshotBadge status={row.snapshotStatus} />
                        </td>
                        <td className="px-4 py-2 text-sm text-neutral-600">
                          {formatDate(row.purgeAfter)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-neutral-600">
                Łącznie: {data.total} | Strona {data.page} z {totalPages}
              </div>
              <div className="flex gap-2 items-center">
                <label className="text-sm text-neutral-600">
                  Na stronę:&nbsp;
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    data-testid="admin-tenant-deletions-limit"
                    className="border border-neutral-300 rounded-md px-2 py-1 text-sm"
                  >
                    {[25, 50, 100, 200].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="admin-tenant-deletions-prev"
                  aria-label="Poprzednia strona"
                  className="rounded-md border border-neutral-300 bg-white px-3 py-1 text-sm disabled:opacity-40"
                >
                  ← Poprzednia
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="admin-tenant-deletions-next"
                  aria-label="Następna strona"
                  className="rounded-md border border-neutral-300 bg-white px-3 py-1 text-sm disabled:opacity-40"
                >
                  Następna →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminTenantDeletions;
